import React, { memo, Fragment, useEffect, useRef, useState, useMemo } from 'react';
import { LayerGroup, Circle, useLeaflet } from 'react-leaflet';
import { scaleSqrt } from 'd3-scale';
import { max } from 'd3-array';
import $ from 'cash-dom';
import styled from '@emotion/styled';

import config from '~/config/article';

const StyledCricle = styled(Circle)`
  stroke-width: 0;
  fill-opacity: 0.8;

  &:hover {
    stroke-width: 1px;
  }

  ${(p) =>
    p.type === 'confirmed' &&
    `
    stroke: ${p.theme.colors.vizred};
  `}

  ${(p) =>
    p.resolved &&
    `
    stroke: ${p.theme.colors.vizgreen};
  `}

  ${(p) =>
    p.resolvedblack &&
    `
    stroke: ${p.theme.colors.vizblack};
  `}

  ${(p) =>
    p.type === 'recovered' &&
    `
    fill: ${p.theme.colors.vizgreen};
    pointer-events: none !important;
  `}

  ${(p) =>
    p.type === 'death' &&
    `
    fill: ${p.theme.colors.vizblack};
    pointer-events: none !important;
  `}

  ${(p) =>
    p.hidden &&
    `
    display: none;
  `}
`;

const getMarkerInBoundingBox = (data, bounds) => {
  if (!data) return [];
  return data.filter(
    (d) =>
      d.lat >= bounds._southWest.lat &&
      d.lat <= bounds._northEast.lat &&
      d.lon >= bounds._southWest.lng &&
      d.lon <= bounds._northEast.lng
  );
};

const MarkerLayer = memo(
  ({
    data,
    onMouseEnter,
    onMouseMove,
    onMouseOut,
    date,
    minRadius = config.map.minRadius,
    maxRadius = config.map.maxRadius,
    maxValue = 0,
    confirmedFillScale = () => '#d14f69',
    showRecoveredCases = true,
  }) => {
    const [isInitialRender, setIsInitialRender] = useState(true);
    const { map } = useLeaflet();
    const loaded = useRef(false);
    useEffect(() => {
      if (data && !loaded.current) {
        window.setTimeout(() => {
          $(document).trigger('map-loaded');
        }, 500);
        loaded.current = true;
      }
    }, [data]);

    useEffect(() => {
      map.on('move', () => {
        setIsInitialRender(false);
      });
    }, [map]);

    const maxConfirmed = Math.max(data && max(data, (d) => d.confirmed), maxValue);

    const radiusScale = scaleSqrt().domain([0, maxConfirmed]).range([minRadius, maxRadius]);

    const markerData = useMemo(() => {
      if (isInitialRender) {
        return getMarkerInBoundingBox(data, map.getBounds()).sort(
          (a, b) => b.confirmed - a.confirmed
        );
      }

      return data.sort((a, b) => b.confirmed - a.confirmed);
    }, [isInitialRender, data]);

    const renderMarker = (d) => {
      if (Number.isNaN(d.lat) || Number.isNaN(d.lon) || !data) {
        return null;
      }

      const merc_fact = Math.max(
        (Math.log(Math.tan((d.lat / 90 + 1) * (Math.PI / 4))) * (180 / Math.PI)) / 50,
        1
      );

      const center = [d.lat, d.lon];
      const confirmedRadius = radiusScale(d.confirmed);
      const innerRadiusScale = scaleSqrt().domain([0, d.confirmed]).range([0, confirmedRadius]);

      const factor = 1 / Math.cos((d.lat * Math.PI) / 180) / 1.589015729065749; // pre-compensate mercator, zero at germany

      // recovered needs to be including death to make up for the area eclipsed
      const recoveredRadius = innerRadiusScale(d.recovered + d.deaths);
      const deathsRadius = innerRadiusScale(d.deaths);

      const showRecoveredCircle = !!(showRecoveredCases && recoveredRadius);

      return (
        <Fragment key={d.label + d.parent}>
          <StyledCricle
            type="confirmed"
            onMouseOver={() => onMouseEnter(d)}
            onClick={() => onMouseEnter(d)}
            onMouseMove={(evt) => onMouseMove(evt.containerPoint)}
            onMouseOut={() => onMouseOut()}
            fillColor={confirmedFillScale(d)}
            center={center}
            radius={~~(confirmedRadius / factor)}
            resolved={d.recovered + d.deaths === d.confirmed && d.recovered > 0}
            resolvedBlack={d.recovered + d.deaths === d.confirmed && d.recovered === 0}
            hidden={d.confirmed <= 0}
          />
          {showRecoveredCircle && (
            <StyledCricle
              type="recovered"
              center={center}
              radius={~~(recoveredRadius / factor)}
              hidden={d.recovered <= 0}
            />
          )}
          {!!deathsRadius && (
            <StyledCricle
              type="death"
              center={center}
              radius={~~(deathsRadius / factor)}
              hidden={d.deaths <= 0}
            />
          )}
        </Fragment>
      );
    };

    if (!data) {
      return null;
    }

    return <LayerGroup key={date}>{markerData.map(renderMarker)}</LayerGroup>;
  }
);

MarkerLayer.displayName = 'MarkerLayer';

export default MarkerLayer;
